<template>
  <spinner v-if="$apollo.loading" />
  <div v-else>
    <h1 class="title-screen">{{ adminText('workflowsAll.documentation_9e9cf32', 'Documentation') }}</h1>
    <div class="document-wrapper">
      <div class="document-container full-width">
        <div class="document-header">
          <img :alt="adminText('workflowsAll.summary_12b71c3', 'Summary')" src="/assets/file.svg">
          <p style="margin-bottom: 0" class="document-type">{{ adminText('workflowsAll.summary_12b71c3', 'Summary') }}</p>
        </div>
        <div class="date-container">
          <date-field
            v-model="summaryFrom"
            :label="adminText('workflowsAll.from_date_3b5d11a', 'From date')"
            :max="summaryTo ? summaryTo.toString() : eventTo.toString()"
            :min="eventCreatedAt.toString()"
          />
          <date-field
            v-model="summaryTo"
            :label="adminText('workflowsAll.to_date_af244da', 'To date')"
            :max="new Date().toISOString().slice(0, 10)"
            :min="summaryFrom ? summaryFrom.toString() : null"
          />
        </div>
        <a :href="getUrl" target="_blank" style="padding: 16px; align-self: flex-end">
          <main-button flat>{{ adminText('workflowsAll.generate_fc45f9b', 'Generate') }}</main-button>
        </a>
      </div>

      <div
        v-if="canGeneratePayoutBundle"
        class="document-container full-width"
        data-test="payout-documents-container"
      >
        <div class="document-header">
          <img :alt="adminText('workflowsAll.payout_bundle_7edee42', 'Payout bundle')" src="/assets/file.svg">
          <p style="margin-bottom: 0" class="document-type">{{ adminText('workflowsAll.organizer_payout_bundle_6dffe16', 'Organizer Payout Bundle') }}</p>
        </div>
        <div class="date-container">
          <date-field
            v-model="payoutFrom"
            :label="adminText('workflowsAll.from_date_optional_6371895', 'From date (optional)')"
            :max="payoutTo || (eventTo ? eventTo.toString() : null)"
            :min="eventCreatedAt ? eventCreatedAt.toString() : undefined"
          />
          <date-field
            v-model="payoutTo"
            :label="adminText('workflowsAll.to_date_optional_30c7a52', 'To date (optional)')"
            :max="new Date().toISOString().slice(0, 10)"
            :min="payoutFrom || (eventCreatedAt ? eventCreatedAt.toString() : undefined)"
          />
        </div>
        <div class="document-actions payout-actions">
          <div>
            {{ adminText('workflowsAll.generate_fiscalized_invoice_calculation_summa_74b9cba', 'Generate fiscalized invoice + calculation & summary PDFs as a single ZIP archive.') }}
            <div class="radio-row">
              <label>
                <input v-model="createInvoice" type="radio" name="create-invoice" :value="true">
                {{ adminText('workflowsAll.create_invoice_now_a87b84f', 'Create invoice now') }}
              </label>
              <label>
                <input v-model="createInvoice" type="radio" name="create-invoice" :value="false">
                {{ adminText('workflowsAll.skip_invoice_preview_only_d80be7c', 'Skip invoice (preview only)') }}
              </label>
            </div>
          </div>
          <main-button
            flat
            :loading="isGeneratingPayout"
            :disabled="isGeneratingPayout"
            @click.native="generatePayoutDocuments"
          >
            {{ adminText('workflowsAll.download_zip_7bd0e4b', 'Download ZIP') }}
          </main-button>
        </div>
        <p v-if="payoutError" class="error-message">{{ workflowLabel(payoutError) }}</p>
      </div>

      <div class="document-container full-width" :class="{ disabled: !legalInvoicesAvailable }">
        <div class="document-header">
          <img :alt="adminText('workflowsAll.summary_12b71c3', 'Summary')" src="/assets/file.svg">
          <p style="margin-bottom: 0" class="document-type">{{ adminText('workflowsAll.r1_invoices_8bf2e93', 'R1 Invoices') }}</p>
        </div>
        <div v-if="legalInvoicesAvailable" class="document-actions">
          <div>
            <span> {{ adminText('workflowsAll.invoice_count_7df6a04', 'Invoice count:') }} </span>
            <span class="invoice-number">
              {{ workflowLabel(legalInvoicesAvailable) }}
            </span>
          </div>
          <router-link
            :to="{
              name: routes.organizer.eventLegalEntityInvoices,
              params: { organizationSlug: organizationSlug, eventId: eventId },
            }"
          >
            <main-button flat>{{ adminText('workflowsAll.open_cf9b770', 'Open') }}</main-button>
          </router-link>
        </div>

        <div v-else class="not-available">{{ adminText('workflowsAll.not_available_d1a17af', 'Not available') }}</div>
      </div>
    </div>
  </div>
</template>
<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import { frontendURL } from '@/utils'
import { GET_EVENT_SETTINGS } from '@/graphql/event.graphql'
import { GET_EVENT_INVOICES_CATEGORIZED } from '../../graphql/purchase.graphql'
import { GET_LOGGED_USER } from '@/graphql/user.graphql'
import { downloadEventPayoutDocuments } from '@/http'

export default {
  mixins: [WorkflowDisplay],
  name: 'documentation-container',
  props: {
    organizationSlug: {
      type: String,
      default: '',
    },
    eventId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      summaryFrom: undefined,
      summaryTo: undefined,
      eventCreatedAt: null,
      eventTo: null,
      legalInvoicesAvailable: false,
      payoutFrom: undefined,
      payoutTo: undefined,
      isGeneratingPayout: false,
      payoutError: null,
      currentUser: null,
      createInvoice: true,
    }
  },
  computed: {
    getUrl() {
      return `${frontendURL}/organizations/${this.organizationSlug}/events/${this.eventId}/summary${
        this.summaryFrom && this.summaryTo
          ? `?from=${this.fullFromDate()}&to=${this.fullToDate()}`
          : ''
      }`
    },
    canGeneratePayoutBundle() {
      return this.currentUser && this.currentUser.type === 'SUPER_ADMIN'
    },
  },
  methods: {
    fullFromDate() {
      if (this.summaryFrom) {
        const date = new Date(this.summaryFrom)
        date.setHours(0, 0, 0, 0) // Set time to 00:00:00

        return date
      }

      return undefined
    },
    fullToDate() {
      if (this.summaryTo) {
        const date = new Date(this.summaryTo)
        date.setHours(22, 59, 59, 999) // Set time to 22:59:59.999

        return date
      }

      return undefined
    },
    async generatePayoutDocuments() {
      if (this.isGeneratingPayout) {
        return
      }
      this.payoutError = null
      this.isGeneratingPayout = true
      try {
        const response = await downloadEventPayoutDocuments(this.eventId, {
          from: this.payoutFrom,
          to: this.payoutTo,
          createInvoice: this.createInvoice,
        })
        const blob = new Blob([response.data], { type: 'application/zip' })
        const disposition = response.headers['content-disposition']
        let filename = 'payout-documents.zip'
        if (disposition) {
          const match = disposition.match(/filename="?([^"]+)"?/)
          if (match && match[1]) {
            filename = match[1]
          }
        } else if (this.organizationSlug) {
          filename = `${this.organizationSlug}-${this.eventId}-payout-docs.zip`
        }
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        const responseMessage =
          error && error.response && error.response.data && error.response.data.message
        const errorMessage = error && error.message
        this.payoutError = responseMessage || errorMessage || 'workflowsAll.failed_to_generate_payout_documents_04e5c40'
      } finally {
        this.isGeneratingPayout = false
      }
    },
  },
  apollo: {
    getEvent: {
      query: GET_EVENT_SETTINGS,
      variables() {
        return {
          id: this.eventId,
        }
      },
      manual: true,
      result({ data, loading }) {
        if (!loading && data) {
          const { to, createdAt } = data.getEvent
          this.eventTo = to
          this.eventCreatedAt = createdAt
        }
      },
    },
    getEventDocumentationData: {
      query: GET_EVENT_INVOICES_CATEGORIZED,
      variables() {
        return {
          eventId: this.eventId,
        }
      },
      manual: true,
      result({ data, loading }) {
        if (!loading && data) {
          const { invoicesCategorized } = data.getEventInvoicesCountCategorized

          this.legalInvoicesAvailable = invoicesCategorized.legalEntityInvoicesCount
        }
      },
    },
    currentUser: {
      query: GET_LOGGED_USER,
      fetchPolicy: 'cache-first',
      result({ data, loading }) {
        if (!loading && data && data.me) {
          this.currentUser = data.me
        }
      },
      error() {
        this.currentUser = null
      },
    },
  },
}
</script>
<style scoped>
.document-wrapper {
  display: grid;
  grid-auto-flow: row;
  gap: 2rem;
}

.date-container {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  gap: 1rem;
  padding: 1.5rem;
}

.date-container div {
  flex: 1;
}

.document-container {
  display: flex;
  width: 100%;
  max-width: 685px;
  flex-direction: column;
  border-radius: 16px;
  border: 2px solid #d9d9d9;
  align-items: flex-start;
  flex-shrink: 0;
}

.document-container.disabled {
  pointer-events: none;
  color: #979797;
}

@media screen and (min-width: 1024px) {
  .date-container {
    flex-direction: row;
  }

  .document-container {
    width: 335px;
  }

  .document-container.full-width {
    width: 100%;
  }
}

.document-header {
  border-radius: 16px 16px 0 0;
  display: flex;
  width: 100%;
  padding: 24px;
  align-items: center;
  flex-direction: row;
  justify-content: flex-start;
  background: #f5f5f5;
  gap: 1rem;
}

.document-type {
  color: #666;
  font-family: Raleway;
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  letter-spacing: -0.72px;
}

.document-actions {
  width: 100%;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}

.document-actions > div {
  font-size: 20px;
  font-weight: 400;
  color: #666666;
}

.document-actions .invoice-number {
  color: #0277bd;
  font-size: 18px;
  font-weight: 700;
  padding: 0 0.5rem;
  margin-left: 8px;
  background-color: #f0f6fa;
}

.document-container .not-available {
  padding: 24px;
  align-self: flex-start;
}

.error-message {
  color: #d32f2f;
  padding: 0 16px 16px;
  font-size: 14px;
}

.payout-actions {
  align-items: center;
}

.radio-row {
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
}

.radio-row label {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 14px;
  color: #555;
}
</style>
